import { Link } from "react-router-dom";
import { ArrowUpRight, Briefcase, Calculator, Gavel, GraduationCap, FileSearch, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";

const SERVICES = [
  {
    icon: Calculator,
    title: "White-Glove IDR Calculation",
    summary: "Hand-calculated, defensible IDR offers built around your case specifics.",
    bullets: [
      "Custom QPA challenge methodology",
      "Geographic & specialty benchmarking",
      "Outlier identification with credible info packets",
      "Offer modeling against IDRE patterns",
    ],
  },
  {
    icon: FileSearch,
    title: "Open Negotiation Management",
    summary: "We run the entire 30-day Open Negotiation window so you don't have to.",
    bullets: [
      "Open Negotiation initiation",
      "Payer follow-up & escalation",
      "Settlement evaluation",
      "Clean handoff to IDR if needed",
    ],
  },
  {
    icon: Gavel,
    title: "Federal IDR Representation",
    summary: "Full-service representation through certified IDR entities.",
    bullets: [
      "IDRE selection strategy",
      "Compliant initiation filing",
      "Credible information assembly",
      "Final offer submission & response",
    ],
  },
  {
    icon: ClipboardList,
    title: "Appeals & Underpayment Recovery",
    summary: "Beyond NSA — ERISA, state surprise law, and contracted underpayments.",
    bullets: [
      "ERISA Level 1 & 2 appeals",
      "State-specific dispute pathways",
      "Contractual underpayment audits",
      "Take-back & recoupment defense",
    ],
  },
  {
    icon: Briefcase,
    title: "Outsourced IDR Operations",
    summary: "Fully delegated IDR operations for groups without in-house bandwidth.",
    bullets: [
      "Dedicated case team",
      "Weekly performance reporting",
      "Payer-by-payer scorecards",
      "Pay-on-results pricing",
    ],
  },
  {
    icon: GraduationCap,
    title: "Training & Advisory",
    summary: "Build in-house IDR muscle with PRISM-led training and advisory.",
    bullets: [
      "NSA compliance workshops",
      "QPA challenge bootcamps",
      "Custom SOP development",
      "Quarterly regulatory briefings",
    ],
  },
];

const PROCESS = [
  { step: "01", title: "Discovery", desc: "We review a sample of EOBs and your payer mix under a signed BAA." },
  { step: "02", title: "Strategy", desc: "Custom IDR playbook tied to your specialty, state, and largest payer counterparties." },
  { step: "03", title: "Execution", desc: "PRISM team and platform handle Open Negotiation, IDR filings, and IDRE submissions." },
  { step: "04", title: "Reporting", desc: "Weekly recovery reports and quarterly business reviews with your finance team." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative bg-white">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20 relative">
          <div className="overline text-slate-500">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light mt-4 text-slate-900 max-w-4xl leading-[1.05]">
            White-glove IDR consulting.
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl">
            Software alone doesn't win disputes — strategy does. PRISM's IDR specialists handle
            the entire arbitration lifecycle on your behalf, with results-based pricing.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-${i}`}
                className="bg-white p-10 group hover:bg-slate-50 transition-colors"
              >
                <s.icon className="h-8 w-8 text-[#0A2540]" />
                <div className="font-display text-xl font-medium mt-6 text-slate-900">{s.title}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.summary}</p>
                <ul className="mt-6 space-y-2 text-sm text-slate-700">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span className="text-[#2563EB]">—</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">Engagement</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light mt-4 text-slate-900">
                How we work.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Most engagements start with a 30-day diagnostic. From there, we move to a
                pay-on-results retainer aligned to net recovery.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="space-y-px bg-slate-200 border border-slate-200">
                {PROCESS.map((p, i) => (
                  <div key={p.step} className="bg-white p-6 grid grid-cols-12 gap-4 items-center" data-testid={`process-${i}`}>
                    <div className="col-span-2 font-display text-3xl font-light text-[#2563EB] tabular-nums">{p.step}</div>
                    <div className="col-span-3 font-display text-lg font-medium text-slate-900">{p.title}</div>
                    <div className="col-span-7 text-sm text-slate-600">{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <img
                src="https://plus.unsplash.com/premium_photo-1673953509982-632a317d1f8c?fm=jpg&q=60&w=1600&auto=format&fit=crop"
                alt="Doctor in white coat and white gloves"
                className="w-full h-[420px] object-cover border border-slate-200"
              />
            </div>
            <div className="lg:col-span-5">
              <div className="overline text-slate-500">Why white-glove</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-light mt-4 text-slate-900">
                Specialists, not generalists.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Our IDR team is staffed by former payer auditors, healthcare attorneys, and
                actuarial analysts. Every dispute is owned by a named lead — not a queue.
              </p>
              <Button
                asChild
                data-testid="services-cta"
                className="mt-8 rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-12 px-6"
              >
                <Link to="/contact">Schedule a strategy call <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
